import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ResponsiveContext, Button, Layer, Anchor, Text, Box, Select, RadioButtonGroup } from 'grommet';
import { FormClose } from 'grommet-icons';

import { Placeholder, ErrorForm } from '../../../../components';
import { TitleField, DescriptionField } from '../../../../components/form';
import { TitleDetail } from '../../../../components/detail';
import { MessageTemplate } from '../message-template/message-template';

import { create, getTemplates, getTemplateDetails, hideFormManagerLayer } from '../../actions/form-manager';
import { changeValues } from '../../../../actions/form';

import visibleWidth from '../../../../constants/layer-width';

import selectCampaignTypeOptions from '../../../../constants/select-campaign-type-options';
import selectCommunicationCategoryOptions from '../../../../constants/select-communication-category';

const FormCreate = (props) => {
  const prevTypeIdRef = useRef();
  const prevCategoryIdRef = useRef();
  const prevTemplateIdRef = useRef();

  const handlePost = useCallback(formParams => {
    const { create } = props;
    create({ formParams });
  }, [props]);

  const enabledButton = useCallback(() => {
    const {
      categoryId,
      typeId,
      templateId
    } = props;

    return categoryId && typeId && templateId;
  }, [props]);

  useEffect(() => {
    const {
      categoryId,
      typeId,
      templateId,

      getTemplates,
      getTemplateDetails,
      toggleLayer,
    } = props;

    if(!toggleLayer || toggleLayer !== 'manager')
      return;

    if ((prevTypeIdRef.current !== typeId || prevCategoryIdRef.current !== categoryId) && categoryId && typeId) {
      getTemplates({ categoryId, typeId });

      prevTypeIdRef.current = typeId;
      prevCategoryIdRef.current = categoryId;
    }

    if (prevTemplateIdRef.current !== templateId && templateId) {
      getTemplateDetails({ id: templateId });

      prevTemplateIdRef.current = templateId;
    }
  }, [props]);

  const {
    categoryId,
    typeId,
    templateId,

    getDetailError,

    templateOptions,
    templateDetails,
    profile,

    toggleLayer,
    loadingPostForm,
    postDataError,

    changeValues,
    hideFormManagerLayer,
  } = props;


  if(!toggleLayer || toggleLayer !== 'manager')
    return null;

  return (
    <ResponsiveContext.Consumer>
      {size => (
        <Layer
          responsive={false}
          full='vertical'
          position='left'
          onClickOutside={hideFormManagerLayer}
          onEsc={hideFormManagerLayer}
        >
          <Box
            fill='vertical'
            width={visibleWidth[size]}
            pad={{ top: 'large', bottom: 'xsmall' }}
            overflow='auto'
            flex
          >
            {getDetailError ? (
              <Placeholder
                title='Ops! Algo deu errado :('
                message='Estamos constrangidos por isso.'
                button={{
                  label: 'Fechar',
                  action: hideFormManagerLayer,
                }}
              />
            ) : null}

            {profile === undefined && !getDetailError ? (
              <Placeholder
                title='Carregando...'
                message='Aguarde que estamos preparando as coisas :)'
              />
            ) : null}

            {profile === null && !getDetailError ? (
              <Placeholder
                title='Ops!'
                message='Configure o perfil de exibição antes de utilizar essa funcionalidade.
                          Acesse Configurações > "Perfil de exibição".'
                button={{
                  label: 'Fechar',
                  action: hideFormManagerLayer,
                }}
              />
            ) : null}

            {profile && !getDetailError ? (
              <>
                <Box
                  direction='row'
                  justify='between'
                  align='baseline'
                  margin={{ horizontal: 'large' }}
                >
                  <TitleDetail value='Configurar comunicações automáticas' />

                  <Button
                    plain
                    title='Fechar'
                    icon={<FormClose />}
                    onClick={hideFormManagerLayer}
                  />
                </Box>


                <Box flex overflow='auto'>
                  {/* Sobre */}
                  <Box
                    gap={ size === 'small' ? 'medium' : 'small' }
                    margin={{
                      vertical: 'small',
                      horizontal: 'large'
                    }}
                    flex={{ shrink: 0 }}
                  >
                    <Text color='dark-2' size='small'>
                      A comunicação automática permite o envio de mensagens personalizadas para seus contatos em datas especiais.
                      Ela acontece de forma automática, sem a necessidade de intervenção manual. Acompanhe os disparos pelo menu Campanhas.
                    </Text>
                  </Box>


                  {/* Categoria comunicação */}
                  <Box
                    margin={{
                      vertical: 'small',
                      horizontal: 'large'
                    }}
                    flex={{ shrink: 0 }}
                    direction={ size === 'small' ? 'column' : 'row' }
                    align='stretch'
                    gap='small'
                  >
                    <Box
                      width={ size === 'small' ? '100vw' : '50vw'}
                      gap='xxsmall'
                    >
                      <TitleField text='Categoria' required />

                      <DescriptionField
                        text='Por enquanto é possível comunicar apenas aniversários. Logo teremos mais opções :)'
                      />
                    </Box>

                    <Box
                      width={ size === 'small' ? '100vw' : '50vw'}
                    >
                      <Select
                        options={selectCommunicationCategoryOptions.filter((opt) => opt.config)}
                        value={categoryId ?? ''}
                        labelKey='name'
                        valueKey={{ key: 'id', reduce: true }}
                        placeholder='Selecione uma opção'
                        title='Selecione o meio de envio'
                        onChange={({ value: nextValue }) =>
                          changeValues({ fieldName: 'categoryId', value: nextValue }, 'formChangeValues')
                        }
                      />

                      <ErrorForm errorsList={postDataError.errors} fieldName='categoryId' />
                    </Box>
                  </Box>


                  {/* meio de comunicação */}
                  <Box
                    margin={{
                      vertical: 'small',
                      horizontal: 'large'
                    }}
                    flex={{ shrink: 0 }}
                    direction={ size === 'small' ? 'column' : 'row' }
                    align='stretch'
                    gap='small'
                  >
                    <Box
                      width={ size === 'small' ? '100vw' : '50vw'}
                      gap='xxsmall'
                    >
                      <TitleField text='Enviar por' required />

                      <DescriptionField
                        text='Marque por qual meio a mensagem será enviada. Logo teremos mais opções ;)'
                      />
                    </Box>

                    <Box
                      width={ size === 'small' ? '100vw' : '50vw'}
                    >
                      <Box
                        round='xxsmall'
                        pad={{
                          vertical: 'small',
                        }}
                        gap='xsmall'
                      >
                        <RadioButtonGroup
                          disabled={!categoryId}
                          name='typeId'
                          value={typeId ?? ''}
                          options={
                            selectCampaignTypeOptions
                              .filter(item => item.config)
                              .map(item => ({
                                  label: item.name,
                                  id: item.id.toString(),
                                  value: item.id.toString(),
                                })
                              )
                          }
                          onChange={event => {
                            changeValues({ fieldName: 'typeId', value: event.target.value }, 'formChangeValues')
                          }}
                        >
                          {(option, { checked }) => {
                            let background = null;
                            let borderColor = 'dark-5';

                            if(checked) {
                              background = 'brand';
                              borderColor = 'brand';
                            }

                            return (
                              <Box
                                direction='row'
                                gap='small'
                                justify='center'
                                align='center'
                              >
                                <Box
                                  width={'20px'}
                                  height={'20px'}
                                  background={background}
                                  round='xxsmall'
                                  border={{
                                    color: borderColor
                                  }}
                                />

                                <Text color='dark-2' size='small'>{option.label}</Text>
                              </Box>
                            )
                          }}
                        </RadioButtonGroup>
                      </Box>

                      <ErrorForm errorsList={postDataError.errors} fieldName='typeId' />
                    </Box>
                  </Box>


                  {/* messages */}
                  {parseInt(typeId) === 2 && (
                    <Box
                      margin={{
                        vertical: 'small',
                        horizontal: 'large'
                      }}
                      flex={{ shrink: 0 }}
                      align='stretch'
                      gap='small'
                    >
                      <Box
                        pad={{
                          vertical: 'small',
                          horizontal: 'small',
                        }}
                        round='small'
                        background='#fff4dd'
                      >
                        <Text size='small' color='status-warning'>
                          Garanta que tenha saldo de SMS para o envio das mensagens automáticas.
                          Acompanhe o saldo pela área do cliente no menu Mais.
                        </Text>
                      </Box>
                    </Box>
                  )}


                  {/* Template Mensagem */}
                  <Box
                    margin={{
                      vertical: 'small',
                      horizontal: 'large'
                    }}
                    direction={ size === 'small' ? 'column' : 'row' }
                    align='stretch'
                    flex={{ shrink: 0 }}
                    gap='small'
                  >
                    <Box
                      width={ size === 'small' ? '100vw' : '50vw' }
                      gap='xxsmall'
                    >
                      <TitleField text='Selecione um modelo' required />

                      <Select
                        options={templateOptions}
                        value={templateId ?? ''}
                        labelKey='name'
                        disabled={!typeId || !categoryId}
                        valueKey={{ key: 'id', reduce: true }}
                        placeholder='Selecione uma opção'
                        title={ !typeId || !categoryId ? 'Selecione os demais campos' : 'Selecione um modelo' }
                        onChange={({ value: nextValue }) =>
                          changeValues({ fieldName: 'templateId', value: nextValue }, 'formChangeValues')
                        }
                      />

                      <ErrorForm errorsList={postDataError.errors} fieldName='templateId' />
                    </Box>

                    <Box
                      width={ size === 'small' ? '100vw' : '50vw' }
                      margin={{ top: 'medium' }}
                    >
                      <MessageTemplate
                        type={templateDetails.typeId || null}
                        template={templateDetails.templateShow || null}
                        templateId={templateId}
                      />
                    </Box>
                  </Box>
                </Box>


                <Box
                  direction='row'
                  pad={{ vertical: 'medium' }}
                  justify='center'
                  align='center'
                  gap='small'
                >
                  <Box width='xsmall'>
                    <Anchor
                      color='dark-3'
                      label='Cancelar'
                      disabled={ loadingPostForm }
                      onClick={ hideFormManagerLayer }
                    />
                  </Box>

                  <Box width='small'>
                    <Button
                      color='brand'
                      primary
                      label={ !loadingPostForm ? 'Salvar' : 'Salvando...' }
                      disabled={ loadingPostForm || !enabledButton() }
                      onClick={() =>
                        handlePost({
                          categoryId,
                          typeId,
                          templateId,
                        })
                      }
                    />
                  </Box>
                </Box>
              </>
            ) : null}
          </Box>
        </Layer>
      )}
    </ResponsiveContext.Consumer>
  );
}

const mapStateToProps = ({
  layerDetailsReducer, layerReducer, formCreateUpdateCopyGeneric, settingsAutoCommunicationReducer: { formManager }
}) => ({
  categoryId: formCreateUpdateCopyGeneric.categoryId,
  typeId: formCreateUpdateCopyGeneric.typeId,
  templateId: formCreateUpdateCopyGeneric.templateId,

  profile: layerDetailsReducer?.detail?.profile,
  getDetailError: layerDetailsReducer.getDetailError,

  templateOptions: formManager.templateList,
  templateDetails: formManager.templateDetails,

  loadingPostForm: formCreateUpdateCopyGeneric.loadingPostForm,
  postDataError: formCreateUpdateCopyGeneric.postDataError,

  toggleLayer: layerReducer.toggleLayer,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    create,
    getTemplates,
    getTemplateDetails,
    changeValues,
    hideFormManagerLayer,
  }, dispatch);


export default connect(mapStateToProps, mapDispatchToProps)(FormCreate);
